//--------------------------------------------------------------------------------------------------
// URLs
//--------------------------------------------------------------------------------------------------

import {
  type PoolParams,
  useDeleteQuery,
  useGetQuery,
  usePostQuery,
  usePutQuery,
  useRouteParams
} from "@quortex/front-common"
import { getPoolUrl } from "@/queries/playPools"
import type { CdnParams, PaginationParams } from "@/queries/types"

/**
 * @returns /pools/${poolUuid}/cdns/${cdnUuid}?org=${orgUuid}
 */
export const getCdnUrl = ({ orgUuid, poolUuid, cdnUuid }: Required<CdnParams>) => {
  const params = new URLSearchParams({ org: orgUuid })
  return `/pools/${poolUuid}/cdns/${cdnUuid}/?${params}`
}

/**
 * @returns /pools/${poolUuid}/cdns/?org=${orgUuid}
 * @returns /pools/${poolUuid}/cdns/?org=${orgUuid}&page=${page}&page_size=${pageSize}
 */
export const getCdnListUrl = ({
  orgUuid,
  poolUuid,
  page,
  pageSize
}: Required<PoolParams> & PaginationParams) => {
  const params = new URLSearchParams({ org: orgUuid })
  if (page) params.set("page", page.toString())
  if (pageSize) params.set("page_size", pageSize.toString())
  return `/pools/${poolUuid}/cdns/?${params}`
}

//--------------------------------------------------------------------------------------------------
// Queries & mutations
//--------------------------------------------------------------------------------------------------

export const useCdn = (params?: CdnParams) => {
  const { orgUuid, poolUuid, cdnUuid } = useRouteParams(params)
  const url = getCdnUrl({ cdnUuid, orgUuid, poolUuid })

  return useGetQuery<api.ott.Cdn>("ott", url)
}

export const useCdnList = (params?: PoolParams & PaginationParams) => {
  const { orgUuid, poolUuid, page, pageSize } = useRouteParams(params)
  const url = getCdnListUrl({ orgUuid, page, pageSize, poolUuid })

  return useGetQuery<api.ott.PaginatedCdnList>("ott", url)
}

export const useAddCdn = (params?: PoolParams) => {
  const mergedParams = useRouteParams(params)
  const poolUrl = getPoolUrl(mergedParams)
  const listUrl = getCdnListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.CdnRequest>
  type TResponse = api.ott.Cdn

  return usePostQuery<TRequest, TResponse>("ott", listUrl, [poolUrl, listUrl], {
    matchAllSearchParams: true
  })
}

export const useUpdateCdn = (params?: CdnParams) => {
  const mergedParams = useRouteParams(params)
  const url = getCdnUrl(mergedParams)
  const listUrl = getCdnListUrl(mergedParams)

  type TRequest = DeepPartial<api.ott.CdnRequest>
  type TResponse = api.ott.Cdn

  return usePutQuery<TRequest, TResponse>("ott", url, [url, listUrl])
}

export const useDeleteCdn = (params?: CdnParams) => {
  const mergedParams = useRouteParams(params)
  const url = getCdnUrl(mergedParams)
  const poolUrl = getPoolUrl(mergedParams)
  const listUrl = getCdnListUrl(mergedParams)

  return useDeleteQuery("ott", url, [poolUrl, listUrl])
}
